import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BlockchainVerificationPanel = ({ reportData, onVerificationComplete }) => {
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [transactionHash, setTransactionHash] = useState('');
  const [blockNumber, setBlockNumber] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [gasUsed, setGasUsed] = useState('');
  const [verificationProgress, setVerificationProgress] = useState(0);

  useEffect(() => {
    if (reportData && Object.keys(reportData)?.length > 0) {
      simulateBlockchainVerification();
    }
  }, [reportData]);

  const simulateBlockchainVerification = async () => {
    setVerificationStatus('processing');
    setVerificationProgress(0);

    // Simulate verification steps
    const steps = [
      { progress: 20, message: 'Generating data hash...' },
      { progress: 40, message: 'Connecting to blockchain network...' },
      { progress: 60, message: 'Creating transaction...' },
      { progress: 80, message: 'Mining block...' },
      { progress: 100, message: 'Verification complete' }
    ];

    for (const step of steps) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setVerificationProgress(step?.progress);
    }

    // Generate mock blockchain data
    const mockHash = `0x${Math.random()?.toString(16)?.substr(2, 64)}`;
    const mockBlock = Math.floor(Math.random() * 1000000) + 15000000;
    const mockTimestamp = new Date()?.toISOString();
    const mockGas = Math.floor(Math.random() * 50000) + 21000;

    setTransactionHash(mockHash);
    setBlockNumber(mockBlock?.toString());
    setTimestamp(mockTimestamp);
    setGasUsed(mockGas?.toString());
    setVerificationStatus('verified');

    if (onVerificationComplete) {
      onVerificationComplete({
        hash: mockHash,
        block: mockBlock,
        timestamp: mockTimestamp,
        gasUsed: mockGas
      });
    }
  };

  const getStatusConfig = () => {
    switch (verificationStatus) {
      case 'pending':
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          icon: 'Clock',
          label: 'Pending Verification'
        };
      case 'processing':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Loader',
          label: 'Processing...'
        };
      case 'verified':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Blockchain Verified'
        };
      case 'failed':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'XCircle',
          label: 'Verification Failed'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          icon: 'Circle',
          label: 'Unknown Status'
        };
    }
  };

  const statusConfig = getStatusConfig();

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const formatHash = (hash) => {
    if (!hash) return '';
    return `${hash?.substring(0, 10)}...${hash?.substring(hash?.length - 8)}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp)?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Athens'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig?.bgColor}`}>
          <Icon 
            name={statusConfig?.icon} 
            size={20} 
            className={`${statusConfig?.color} ${verificationStatus === 'processing' ? 'animate-spin' : ''}`}
          />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Blockchain Verification</h3>
          <p className="text-sm text-muted-foreground">Immutable record creation and verification</p>
        </div>
      </div>
      {/* Status Display */}
      <div className={`p-4 rounded-lg ${statusConfig?.bgColor} mb-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name={statusConfig?.icon} size={16} className={statusConfig?.color} />
            <span className={`font-medium ${statusConfig?.color}`}>
              {statusConfig?.label}
            </span>
          </div>
          {verificationStatus === 'processing' && (
            <span className="text-sm text-muted-foreground">
              {verificationProgress}%
            </span>
          )}
        </div>
        
        {verificationStatus === 'processing' && (
          <div className="mt-3">
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-warning h-2 rounded-full transition-all duration-300"
                style={{ width: `${verificationProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>
      {/* Verification Details */}
      {verificationStatus === 'verified' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Transaction Hash */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Transaction Hash</label>
              <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-foreground flex-1">
                  {formatHash(transactionHash)}
                </code>
                <button
                  onClick={() => copyToClipboard(transactionHash)}
                  className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  <Icon name="Copy" size={16} />
                </button>
              </div>
            </div>

            {/* Block Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Block Number</label>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-foreground">
                  #{blockNumber}
                </code>
              </div>
            </div>

            {/* Timestamp */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Verification Time</label>
              <div className="p-3 bg-muted rounded-lg">
                <span className="text-sm text-foreground">
                  {formatTimestamp(timestamp)}
                </span>
              </div>
            </div>

            {/* Gas Used */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Gas Used</label>
              <div className="p-3 bg-muted rounded-lg">
                <span className="text-sm text-foreground">
                  {gasUsed} gas
                </span>
              </div>
            </div>
          </div>

          {/* Data Integrity Hash */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Data Integrity Hash</label>
            <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
              <code className="text-xs font-mono text-foreground flex-1 break-all">
                sha256: {transactionHash}
              </code>
              <button
                onClick={() => copyToClipboard(`sha256: ${transactionHash}`)}
                className="text-muted-foreground hover:text-foreground transition-colors duration-150"
              >
                <Icon name="Copy" size={16} />
              </button>
            </div>
          </div>

          {/* Verification Actions */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => window.open(`https://etherscan.io/tx/${transactionHash}`, '_blank')}
            >
              View on Explorer
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
            >
              Download Certificate
            </Button>
          </div>
        </div>
      )}
      {/* Verification Failed */}
      {verificationStatus === 'failed' && (
        <div className="text-center py-6">
          <Icon name="XCircle" size={48} className="text-error mx-auto mb-3" />
          <p className="text-error font-medium mb-2">Blockchain verification failed</p>
          <p className="text-sm text-muted-foreground mb-4">
            Unable to create immutable record. Please try again.
          </p>
          <Button
            variant="outline"
            onClick={simulateBlockchainVerification}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Retry Verification
          </Button>
        </div>
      )}
      {/* Compliance Notice */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              EMA Compliance Notice
            </h4>
            <p className="text-xs text-muted-foreground">
              This adverse event report has been recorded on an immutable blockchain ledger 
              in compliance with EU pharmaceutical regulations and EMA guidelines for 
              pharmacovigilance data integrity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockchainVerificationPanel;